import type { ReactNode } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

const SECTION_TONES = {
  plain: "px-4 section-y",
  white: "bg-white px-4 section-y dark:bg-surface-dark",
  soft: "bg-surface-light px-4 section-y dark:bg-surface-dark-raised",
  navy: "bg-brand-navy px-4 section-y text-white dark:bg-surface-dark-deep",
} as const;

type SectionTone = keyof typeof SECTION_TONES;

export const ServiceSection = ({
  tone = "white",
  id,
  className,
  children,
}: {
  tone?: SectionTone;
  id?: string;
  className?: string;
  children: ReactNode;
}) => (
  <section id={id} className={cn(SECTION_TONES[tone], className)}>
    {children}
  </section>
);

export const SectionHeader = ({
  title,
  subtitle,
  as: Tag = "h3",
}: {
  title: string;
  subtitle?: string;
  as?: "h2" | "h3";
}) => (
  <div className="text-center">
    <Tag className="text-2xl font-bold text-brand-navy dark:text-white">{title}</Tag>
    {subtitle && <p className="mt-2 text-gray-700 dark:text-gray-300">{subtitle}</p>}
  </div>
);

const OUTLINE_LIGHT_CLASS =
  "rounded-lg border-2 border-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-lg text-white bg-transparent hover:bg-white hover:text-brand-navy transition-colors";

type CtaLink = { href: string; label: string };

export const CtaButtons = ({
  primary,
  secondary,
  className,
}: {
  primary: CtaLink;
  secondary: CtaLink;
  className?: string;
}) => (
  <div className={cn("flex flex-wrap justify-center", className)}>
    <Button asChild className="px-8 py-3 text-lg uppercase">
      <Link href={primary.href}>{primary.label}</Link>
    </Button>
    <Button asChild className={OUTLINE_LIGHT_CLASS}>
      <Link href={secondary.href}>{secondary.label}</Link>
    </Button>
  </div>
);

export const CtaBand = ({
  title,
  subtitle,
  primary,
  secondary,
}: {
  title: string;
  subtitle: string;
  primary: CtaLink;
  secondary: CtaLink;
}) => (
  <ServiceSection tone="navy">
    <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 text-center">
      <h3 className="text-3xl font-black">{title}</h3>
      <p className="max-w-2xl text-lg text-mist">{subtitle}</p>
      <CtaButtons primary={primary} secondary={secondary} className="gap-4" />
    </div>
  </ServiceSection>
);

const SEO_TEXT_CLASS = "text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed text-justify hyphens-auto";

/** Intro + headings with paragraphs, optionally next to an image. */
export const SeoBlock = ({
  as: Tag,
  headings = [],
  paragraphs = [],
  intro,
  image,
  imageAlt,
  reverse = false,
}: {
  as: "h2" | "h3";
  headings?: string[];
  paragraphs?: string[];
  intro?: string;
  image?: string;
  imageAlt: string;
  reverse?: boolean;
}) => {
  const isH2 = Tag === "h2";

  return (
    <div
      className={
        image
          ? cn(
              "flex flex-col gap-8 lg:items-start lg:gap-12",
              reverse ? "lg:flex-row-reverse" : "lg:flex-row",
            )
          : "space-y-4"
      }
    >
      <div className="space-y-4 min-w-0 flex-1">
        {intro && <p className={SEO_TEXT_CLASS}>{intro}</p>}
        {headings.map((heading, i) => (
          <div key={heading} className="space-y-2">
            <Tag
              className={cn(
                "font-semibold text-brand-navy",
                isH2
                  ? "text-base sm:text-lg dark:text-white"
                  : "text-sm sm:text-base dark:text-gray-200",
              )}
            >
              {heading}
            </Tag>
            {paragraphs[i] && (
              <p className={cn(SEO_TEXT_CLASS, isH2 && "whitespace-pre-line")}>{paragraphs[i]}</p>
            )}
          </div>
        ))}
      </div>
      {image && (
        <div className="shrink-0 lg:w-[380px] xl:w-[420px]">
          <div className="overflow-hidden rounded-card shadow-card">
            <Image
              src={image}
              alt={imageAlt}
              width={840}
              height={630}
              className="h-auto w-full object-cover"
              sizes="(min-width: 1024px) 420px, 100vw"
            />
          </div>
        </div>
      )}
    </div>
  );
};
